import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { SupplierPrice, SupplierPriceData } from '../utils/supplierPrices';
import { 
  User, 
  Product, 
  Order, 
  ContactMessage, 
  LoginRequest, 
  RegisterRequest, 
  AuthResponse,
  RegisterResponse,
  ContactFormData,
  OrderFormData,
  CurrencyRate,
  CurrencyConversion
} from '../types';

export interface StockWarning { productName: string; requested: number; available: number; shortfall: number; }

export interface LabelTemplate {
  id: number;
  name: string;
  payload: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

const parseStockWarningsHeader = (raw: unknown): StockWarning[] => {
  if (!raw || typeof raw !== 'string') return [];
  try {
    const parsed = JSON.parse(decodeURIComponent(raw));
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

class ApiService {
  private api: AxiosInstance;
  private readonly apiBaseUrl: string;

  constructor() {
    // In production, Nginx proxies this same-origin path to the private backend.
    // This keeps the backend hostname out of the browser bundle.
    const defaultApiBaseUrl = '/api';
    const envApiBaseUrl = (process.env.REACT_APP_API_URL || '').trim();
    const normalizeApiBaseUrl = (rawUrl: string): string => {
      const cleaned = rawUrl.replace(/\/+$/, '');
      if (!cleaned) return cleaned;
      return cleaned.endsWith('/api') ? cleaned : `${cleaned}/api`;
    };
    const apiBaseUrl = normalizeApiBaseUrl(envApiBaseUrl || defaultApiBaseUrl);
    this.apiBaseUrl = apiBaseUrl;

    const apiTimeoutMs = Number(process.env.REACT_APP_API_TIMEOUT_MS) || 10000;
    this.api = axios.create({
      baseURL: apiBaseUrl,
      timeout: apiTimeoutMs,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Interceptor para agregar token de autenticación
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Interceptor para manejar respuestas
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        const requestPath = String(error?.config?.url || '');
        const isAuthLoginRequest = requestPath.includes('/auth/login') || requestPath.includes('/auth/register');
        if (error.response?.status === 401 && !isAuthLoginRequest) {
          // Token expirado o inválido
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  private getApiErrorMessage(error: any, fallbackMessage: string): string {
    const status = error?.response?.status;
    const detail = error?.response?.data?.detail || error?.response?.data?.message;
    if (status === 401 && detail) return String(detail);
    if (status === 401) return 'Credenciales inválidas.';
    if (status === 404) return `Endpoint no encontrado (${this.apiBaseUrl}). Verifica que la URL incluya /api.`;
    if (status >= 500) return 'Error interno del servidor. Revisa logs del backend.';
    if (error?.code === 'ERR_NETWORK' || !error?.response) return 'No se pudo conectar con la API (CORS/red/servidor caído).';
    if (detail) return String(detail);
    return fallbackMessage;
  }

  // Autenticación
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    try {
      const response: AxiosResponse<AuthResponse> = await this.api.post('/auth/login', credentials);
      return response.data;
    } catch (error: any) {
      throw new Error(this.getApiErrorMessage(error, 'Error al iniciar sesión'));
    }
  }

  async register(userData: RegisterRequest): Promise<RegisterResponse> {
    const response: AxiosResponse<RegisterResponse> = await this.api.post('/auth/register', userData);
    return response.data;
  }

  async getProfile(): Promise<{ user: User }> {
    const response: AxiosResponse<{ user: User }> = await this.api.get('/auth/profile');
    return response.data;
  }

  async updateProfile(userData: Partial<User>): Promise<{ message: string; user: User }> {
    const response: AxiosResponse<{ message: string; user: User }> = await this.api.put('/auth/profile', userData);
    return response.data;
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<{ message: string }> {
    const response: AxiosResponse<{ message: string }> = await this.api.put('/auth/change-password', {
      currentPassword,
      newPassword
    });
    return response.data;
  }

  async verifyToken(): Promise<{ valid: boolean; user: User }> {
    const response: AxiosResponse<{ valid: boolean; user: User }> = await this.api.get('/auth/verify');
    return response.data;
  }

  // Productos
  async getProducts(params?: { category?: string; featured?: boolean; limit?: number; includeInactive?: boolean }): Promise<{ products: Product[]; total: number }> {
    const response: AxiosResponse<{ products: Product[]; total: number }> = await this.api.get('/products', { params });
    return response.data;
  }

  async getProduct(id: number): Promise<{ product: Product }> {
    const response: AxiosResponse<{ product: Product }> = await this.api.get(`/products/${id}`);
    return response.data;
  }

  async getProductCategories(): Promise<{ categories: Array<{ value: string; label: string }> }> {
    const response: AxiosResponse<{ categories: Array<{ value: string; label: string }> }> = await this.api.get('/products/categories/list');
    return response.data;
  }

  async createProduct(productData: Partial<Product>): Promise<{ message: string; product: Product }> {
    const response: AxiosResponse<{ message: string; product: Product }> = await this.api.post('/products', productData);
    return response.data;
  }

  async uploadProductImage(file: File): Promise<{ message: string; image: string; imageUrl: string }> {
    const formData = new FormData();
    formData.append('image', file);
    const response: AxiosResponse<{ message: string; image: string; imageUrl: string }> = await this.api.post('/products/upload-image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }

  async updateProduct(id: number, productData: Partial<Product>): Promise<{ message: string; product: Product }> {
    const response: AxiosResponse<{ message: string; product: Product }> = await this.api.put(`/products/${id}`, productData);
    return response.data;
  }

  async deleteProduct(id: number): Promise<{ message: string }> {
    const response: AxiosResponse<{ message: string }> = await this.api.delete(`/products/${id}`);
    return response.data;
  }

  // Pedidos
  async createOrder(orderData: OrderFormData): Promise<{ message: string; order: Order }> {
    const response: AxiosResponse<{ message: string; order: Order }> = await this.api.post('/orders', orderData);
    return response.data;
  }

  async getCompanyOrderTemplate(): Promise<{ products: any[] }> {
    return (await this.api.get('/orders/company/template')).data;
  }

  async createCompanyOrder(payload: { products: any[]; notes?: string; saveAsTemplate?: boolean }): Promise<{ message: string; order: Order }> {
    return (await this.api.post('/orders/company', payload)).data;
  }

  async generateOrderReceipt(id: number): Promise<{ message: string; receiptId: number; receiptNumber: string; stockWarnings: StockWarning[] }> {
    return (await this.api.post(`/orders/${id}/generate-receipt`)).data;
  }
  async getMyOrders(params?: { page?: number; limit?: number; status?: string }): Promise<{ orders: Order[]; total: number; page: number; totalPages: number }> {
    const response: AxiosResponse<{ orders: Order[]; total: number; page: number; totalPages: number }> = await this.api.get('/orders/my-orders', { params });
    return response.data;
  }

  async getOrder(id: number): Promise<{ order: Order }> {
    const response: AxiosResponse<{ order: Order }> = await this.api.get(`/orders/${id}`);
    return response.data;
  }

  async getOrders(params?: { page?: number; limit?: number; status?: string }): Promise<{ orders: Order[]; total: number; page: number; totalPages: number }> {
    const response: AxiosResponse<{ orders: Order[]; total: number; page: number; totalPages: number }> = await this.api.get('/orders', { params });
    return response.data;
  }

  async updateOrderStatus(id: number, status: string): Promise<{ message: string }> {
    const response: AxiosResponse<{ message: string }> = await this.api.put(`/orders/${id}/status`, { status });
    return response.data;
  }

  async getOrderStats(): Promise<{ total: number; pending: number; confirmed: number; inProduction: number; completed: number; cancelled: number; recent: number; totalSales: number }> {
    const response: AxiosResponse<{ total: number; pending: number; confirmed: number; inProduction: number; completed: number; cancelled: number; recent: number; totalSales: number }> = await this.api.get('/orders/stats/overview');
    return response.data;
  }

  async getUsers(params?: { role?: string; page?: number; limit?: number }): Promise<{ users: User[]; total: number; page: number; totalPages: number }> {
    const response: AxiosResponse<{ users: User[]; total: number; page: number; totalPages: number }> = await this.api.get('/users', { params });
    return response.data;
  }

  async createUserByAdmin(userData: Partial<User> & { password?: string }): Promise<{ message: string; user: User }> {
    const response: AxiosResponse<{ message: string; user: User }> = await this.api.post('/users', userData);
    return response.data;
  }

  async toggleUserStatus(id: number): Promise<{ message: string; user: User }> {
    const response: AxiosResponse<{ message: string; user: User }> = await this.api.put(`/users/${id}/toggle-status`);
    return response.data;
  }

  async updateUserByAdmin(id: number, userData: Partial<User>): Promise<{ message: string; user: User }> {
    const response: AxiosResponse<{ message: string; user: User }> = await this.api.put(`/users/${id}`, userData);
    return response.data;
  }

  async updateUserPasswordByAdmin(id: number, newPassword: string): Promise<{ message: string }> {
    const response: AxiosResponse<{ message: string }> = await this.api.put(`/users/${id}/password`, { newPassword });
    return response.data;
  }

  async deleteUserByAdmin(id: number): Promise<{ message: string }> {
    const response: AxiosResponse<{ message: string }> = await this.api.delete(`/users/${id}`);
    return response.data;
  }

  async getUserStats(): Promise<{ total: number; active: number; inactive: number; admins: number; clients: number; recent: number }> {
    const response: AxiosResponse<{ total: number; active: number; inactive: number; admins: number; clients: number; recent: number }> = await this.api.get('/users/stats/overview');
    return response.data;
  }

  // Contacto
  async sendContactMessage(messageData: ContactFormData): Promise<{ message: string; messageId: number }> {
    const response: AxiosResponse<{ message: string; messageId: number }> = await this.api.post('/contact', messageData);
    return response.data;
  }

  async getContactMessages(params?: { status?: string; subject?: string; page?: number; limit?: number }): Promise<{ messages: ContactMessage[]; total: number; page: number; totalPages: number }> {
    const response: AxiosResponse<{ messages: ContactMessage[]; total: number; page: number; totalPages: number }> = await this.api.get('/contact', { params });
    return response.data;
  }

  async markContactMessageRead(id: number): Promise<{ message: string }> {
    const response: AxiosResponse<{ message: string }> = await this.api.put(`/contact/${id}/read`);
    return response.data;
  }

  async respondContactMessage(
    id: number,
    responseText: string,
    attachment?: File | null,
    quoteQuantity?: string,
    quotePrice?: string
  ): Promise<{ message: string }> {
    let response: AxiosResponse<{ message: string }>;
    if (attachment) {
      const formData = new FormData();
      formData.append('response', responseText);
      formData.append('attachment', attachment);
      if (quoteQuantity) formData.append('quoteQuantity', quoteQuantity);
      if (quotePrice) formData.append('quotePrice', quotePrice);
      response = await this.api.put(`/contact/${id}/respond`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    } else {
      response = await this.api.put(`/contact/${id}/respond`, {
        response: responseText,
        quoteQuantity: quoteQuantity || '',
        quotePrice: quotePrice || ''
      });
    }
    return response.data;
  }

  async deleteContactMessage(id: number): Promise<{ message: string }> {
    const response: AxiosResponse<{ message: string }> = await this.api.delete(`/contact/${id}`);
    return response.data;
  }

  async getContactStats(): Promise<{ total: number; new: number; read: number; responded: number; cotizacion: number; informacion: number }> {
    const response: AxiosResponse<{ total: number; new: number; read: number; responded: number; cotizacion: number; informacion: number }> = await this.api.get('/contact/stats/overview');
    return response.data;
  }

  async getNextQuoteNumber(): Promise<{ quoteNumber: string }> {
    const response: AxiosResponse<{ quoteNumber: string }> = await this.api.get('/quotes/next-number');
    return response.data;
  }

  async generateQuotePdf(payload: any): Promise<{ blob: Blob; filename: string; quoteNumber?: string }> {
    const pdfTimeoutMs = Number(process.env.REACT_APP_PDF_TIMEOUT_MS) || 60000;
    const response = await this.api.post('/quotes/pdf', payload, {
      responseType: 'blob',
      timeout: pdfTimeoutMs,
    });
    const contentDisposition = String(response.headers?.['content-disposition'] || '');
    const match = contentDisposition.match(/filename="?([^"]+)"?/i);
    const filename = match?.[1] || `presupuesto-polcen-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.pdf`;
    const quoteNumber = String(response.headers?.['x-quote-number'] || '');
    return { blob: response.data as Blob, filename, quoteNumber };
  }

  async getNextReceiptNumber(): Promise<{ receiptNumber: string }> {
    const response: AxiosResponse<{ receiptNumber: string }> = await this.api.get('/receipts/next-number');
    return response.data;
  }

  async generateReceiptPdf(payload: any): Promise<{ blob: Blob; filename: string; receiptNumber?: string; stockWarnings: StockWarning[] }> {
    const pdfTimeoutMs = Number(process.env.REACT_APP_PDF_TIMEOUT_MS) || 60000;
    let response: AxiosResponse<Blob>;
    try {
      response = await this.api.post('/receipts/pdf', payload, {
        responseType: 'blob',
        timeout: pdfTimeoutMs,
      });
    } catch (error: any) {
      // With responseType blob the server's error message arrives as a Blob: read it so the user sees the reason.
      const data = error?.response?.data;
      if (data instanceof Blob) {
        try { error.response.data = JSON.parse(await data.text()); } catch { /* not JSON: keep the generic message */ }
      }
      throw error;
    }
    const contentDisposition = String(response.headers?.['content-disposition'] || '');
    const match = contentDisposition.match(/filename="?([^"]+)"?/i);
    const filename = match?.[1] || `remito-polcen-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.pdf`;
    const receiptNumber = String(response.headers?.['x-receipt-number'] || '');
    const stockWarnings = parseStockWarningsHeader(response.headers?.['x-stock-warnings']);
    return { blob: response.data as Blob, filename, receiptNumber, stockWarnings };
  }

  async generateLabelsPdf(payload: {
    labelWidthMm?: number;
    labelHeightMm?: number;
    quantity?: number;
    showBorder?: boolean;
    marginMm?: number;
    gapMm?: number;
    elements?: Array<Record<string, unknown>>;
    // compat formato viejo
    line1?: string;
    line2?: string;
    line3?: string;
    recycleCode?: string;
    showLogo?: boolean;
  }): Promise<{ blob: Blob; filename: string }> {
    const pdfTimeoutMs = Number(process.env.REACT_APP_PDF_TIMEOUT_MS) || 60000;
    const response = await this.api.post('/labels/pdf', payload, {
      responseType: 'blob',
      timeout: pdfTimeoutMs,
    });
    const contentDisposition = String(response.headers?.['content-disposition'] || '');
    const match = contentDisposition.match(/filename="?([^"]+)"?/i);
    const filename = match?.[1] || 'etiquetas-polcen.pdf';
    return { blob: response.data as Blob, filename };
  }

  async getLabelTemplates(): Promise<{ templates: LabelTemplate[] }> {
    const response = await this.api.get('/labels/templates');
    return response.data;
  }

  async createLabelTemplate(name: string, payload: Record<string, unknown>): Promise<{ template: LabelTemplate }> {
    const response = await this.api.post('/labels/templates', { name, payload });
    return response.data;
  }

  async updateLabelTemplate(id: number, data: { name?: string; payload?: Record<string, unknown> }): Promise<{ template: LabelTemplate }> {
    const response = await this.api.put(`/labels/templates/${id}`, data);
    return response.data;
  }

  async deleteLabelTemplate(id: number): Promise<{ message: string }> {
    const response = await this.api.delete(`/labels/templates/${id}`);
    return response.data;
  }

  async getReceipts(params: { page?: number; limit?: number; clientId?: number; includePayments?: boolean } = {}): Promise<{ receipts: any[]; total: number; page: number; totalPages: number }> {
    const response = await this.api.get('/receipts', { params });
    return response.data;
  }

  async getReceiptCountsByClient(): Promise<{ counts: Record<string, number> }> {
    const response = await this.api.get('/receipts/counts-by-client');
    return response.data;
  }

  async getMonthlyOrders(params: { month?: string; clientId?: number } = {}): Promise<{ orders: any[] }> {
    return (await this.api.get('/admin/monthly-orders', { params })).data;
  }

  async createMonthlyOrder(payload: Record<string, any>): Promise<{ order: any }> {
    return (await this.api.post('/admin/monthly-orders', payload)).data;
  }

  async updateMonthlyOrder(id: number, payload: Record<string, any>): Promise<{ order: any }> {
    return (await this.api.put(`/admin/monthly-orders/${id}`, payload)).data;
  }

  async deleteMonthlyOrder(id: number): Promise<{ message: string }> {
    return (await this.api.delete(`/admin/monthly-orders/${id}`)).data;
  }

  async getMyReceipts(params: { page?: number; limit?: number } = {}): Promise<{ receipts: any[]; total: number; page: number; totalPages: number }> {
    const response = await this.api.get('/receipts/mine', { params });
    return response.data;
  }

  async downloadReceiptPdf(id: number): Promise<{ blob: Blob; filename: string }> {
    const response = await this.api.get(`/receipts/${id}/pdf`, { responseType: 'blob' });
    const contentDisposition = String(response.headers?.['content-disposition'] || '');
    const match = contentDisposition.match(/filename="?([^"]+)"?/i);
    const filename = match?.[1] || `remito-polcen-${id}.pdf`;
    return { blob: response.data as Blob, filename };
  }

  async convertReceiptToDelivery(id: number): Promise<{ message: string; receipt: any }> {
    const response = await this.api.put(`/receipts/${id}/convert-to-delivery`);
    return response.data;
  }
  async deleteReceipt(id: number): Promise<{ message: string }> {
    const response = await this.api.delete(`/receipts/${id}`);
    return response.data;
  }
  async updateReceiptDeliveryDate(id: number, deliveryDate: string): Promise<{ message: string; deliveryDate: string }> {
    return (await this.api.put(`/receipts/${id}/delivery-date`, { deliveryDate })).data;
  }

  async updateReceiptClient(id: number, clientId: number | null): Promise<{ message: string; receipt: any }> {
    const response = await this.api.put(`/receipts/${id}/client`, { clientId });
    return response.data;
  }

  async updateReceiptMonthlyOrder(id: number, monthlyOrderId: number | null): Promise<{ message: string; monthlyOrderId: number | null }> {
    return (await this.api.put(`/receipts/${id}/monthly-order`, { monthlyOrderId })).data;
  }

  async updateReceiptItems(id: number, items: Array<Record<string, any>>): Promise<{ message: string; concept: string; items: string[]; deductWarehouseStock: boolean }> {
    const response = await this.api.put(`/receipts/${id}/items`, { items });
    return response.data;
  }

  // Conversión de moneda
  async getMyStock(): Promise<{ items: any[]; movements: any[] }> {
    return (await this.api.get('/stock/mine')).data;
  }

  async createMyStockItem(payload: { productName: string; quantity: number; minimumStock: number }): Promise<{ item: any }> {
    return (await this.api.post('/stock/mine/items', payload)).data;
  }

  async createMyStockMovement(id: number, payload: { type: 'consumo' | 'ajuste'; quantity: number; unit?: 'packs' | 'bolsas'; note?: string }): Promise<{ item: any }> {
    return (await this.api.post(`/stock/mine/${id}/movement`, payload)).data;
  }

  async updateMyStockMinimum(id: number, minimumStock: number): Promise<{ item: any }> {
    return (await this.api.put(`/stock/mine/${id}/minimum`, { minimumStock })).data;
  }

  async getAdminStock(params: { clientId?: number } = {}): Promise<{ items: any[]; lowStockCount: number }> {
    return (await this.api.get('/admin/stock', { params: { client_id: params.clientId } })).data;
  }

  async updateAdminStockMinimum(id: number, minimumPacks: number): Promise<{ message: string; item: any }> {
    return (await this.api.put(`/admin/stock/${id}/minimum`, { minimumPacks })).data;
  }

  async reconcileStock(): Promise<{ message: string; supplierReceipts: number; deliveryRemitos: number }> {
    return (await this.api.post('/admin/stock/reconcile')).data;
  }

  // Proveedores
  async getSuppliers(): Promise<{ suppliers: any[] }> {
    return (await this.api.get('/admin/suppliers')).data;
  }

  async getBusinessStats(): Promise<{
    reconciliation?: import('../components/admin/ArticleReconciliation').ReconciliationData;
    inventory: { commitments: Array<{clientId:number;clientName:string;month:string;productName:string;pendingQuantity:number;invoicedQuantity:number;invoicePendingQuantity:number;invoicePendingNet:number;invoicePendingVat:number;invoicePendingGross:number}>; advances:Array<{clientId:number;clientName:string;productName:string;quantity:number}>; unlinkedDeliveries:Array<{clientId:number;clientName:string;productName:string;quantity:number}>; legacyInvoiceCount:number; totalValue: number; unpricedQuantity: number; totalSalesNet: number; totalSalesVat: number; totalSalesGross: number; totalSalesLessCost: number; unpricedSalesQuantity: number; products: Array<{ productName: string; quantity: number; reservedQuantity:number; freeQuantity:number; pricedQuantity: number; unpricedQuantity: number; averageCost: number | null; value: number; salePriceNet: number | null; salePriceGross: number | null; salePriceCount: number; salesNet: number | null; salesVat: number | null; salesGross: number | null; salesLessCost: number | null }> };
    products: Array<{ productName: string; productColor: string; purchased: number; delivered: number }>;
    suppliers: Array<{ supplierId: number; name: string; total: number; receiptCount: number }>;
    monthly: Array<{ month: string; purchased: number; invoiced: number }>;
    profit?: { totalNetSales: number; totalCost: number; totalProfit: number; totalQuantity: number; pricedQuantity: number; unpricedQuantity: number; actualCostQuantity: number; snapshotCostQuantity: number; unpricedSaleQuantity: number; unpricedCostQuantity: number; unlinkedQuantity: number; receiptCount: number; unlinkedReceiptCount: number; coveragePercent: number; months: Array<{ month: string; netSales: number; cost: number; profit: number; totalQuantity: number; pricedQuantity: number; unpricedQuantity: number; actualCostQuantity: number; snapshotCostQuantity: number; unpricedSaleQuantity: number; unpricedCostQuantity: number; unlinkedQuantity: number; receiptCount: number; unlinkedReceiptCount: number; products: Array<{ productName: string; quantity: number; pricedQuantity: number; netSales: number; cost: number; profit: number }> }> };
    cashFlow?: { clientDocuments: number; clientCommitted: number; clientCollected: number; clientCommercialBalance: number; clientCashBalance: number; supplierDocuments: number; supplierCommitted: number; supplierPaid: number; supplierCommercialBalance: number; supplierCashBalance: number; realizedCashNet: number; pendingCheckInflow: number; pendingCheckOutflow: number; projectedCashNet: number; schedule: Array<{ bucket: string; inflow: number; outflow: number; net: number }> };
    clients: Array<{ clientId: number; name: string; hasData: boolean; invoiced: number; invoicedNet: number; collected: number; balance: number; estimatedSales: number; estimatedCost: number; estimatedMargin: number }>;
    summary: { totalPurchased: number; totalInvoiced: number; totalInvoicedNet: number; estimatedNetDocuments: number; totalEstimatedSales: number; totalEstimatedCost: number; totalEstimatedMargin: number; clientsWithoutData: number; clientsTotal: number };
  }> {
    return (await this.api.get('/admin/business-stats')).data;
  }

  async createSupplier(payload: { name: string; contactName?: string; phone?: string; email?: string; cuit?: string; address?: string; notes?: string }): Promise<{ message: string; supplier: any }> {
    return (await this.api.post('/admin/suppliers', payload)).data;
  }

  async updateSupplier(id: number, payload: { name?: string; contactName?: string; phone?: string; email?: string; cuit?: string; address?: string; notes?: string; isActive?: boolean }): Promise<{ message: string; supplier: any }> {
    return (await this.api.put(`/admin/suppliers/${id}`, payload)).data;
  }

  async toggleSupplierStatus(id: number): Promise<{ message: string; supplier: any }> {
    return (await this.api.put(`/admin/suppliers/${id}/toggle-status`)).data;
  }

  async deleteSupplier(id: number): Promise<{ message: string }> {
    return (await this.api.delete(`/admin/suppliers/${id}`)).data;
  }

  // Stock propio (almacén)
  async getWarehouseStock(): Promise<{ items: any[]; movements: any[]; lowStockCount: number }> {
    return (await this.api.get('/admin/warehouse-stock')).data;
  }

  async createWarehouseStockEntry(payload: { productName: string; quantity: number; supplierId?: number | null; note?: string }): Promise<{ item: any }> {
    return (await this.api.post('/admin/warehouse-stock/entrada', payload)).data;
  }

  async checkWarehouseStockNegatives(): Promise<{ items: Array<{ productName: string; productColor: string; productMicrons: number | null; shownBags: number; realBags: number; floorBags: number; currentlyNegative: boolean }>; checkedAt: string }> {
    return (await this.api.post('/admin/warehouse-stock/verificar-negativos')).data;
  }

  async getWarehouseStockItemMovements(id: number): Promise<{ item: any; movements: Array<{ id: number; type: string; quantityDelta: number; balanceAfter: number; note: string; createdAt: string; supplierName: string; supplierReceiptNumber: string; customerName: string; clientReceiptNumber: string; receiptId: number | null; userName: string }> }> {
    return (await this.api.get(`/admin/warehouse-stock/${id}/movimientos`)).data;
  }

  async createWarehouseStockMovement(id: number, payload: { type: 'ajuste' | 'merma'; quantity: number; note?: string }): Promise<{ item: any }> {
    return (await this.api.post(`/admin/warehouse-stock/${id}/movement`, payload)).data;
  }

  async updateWarehouseStockMinimum(id: number, minimumStock: number): Promise<{ item: any }> {
    return (await this.api.put(`/admin/warehouse-stock/${id}/minimum`, { minimumStock })).data;
  }

  async updateWarehouseStockMicrons(id: number, microns: number | null): Promise<{ item: any }> {
    return (await this.api.put(`/admin/warehouse-stock/${id}/microns`, { microns })).data;
  }

  async getStockReport(params: { clientId?: number; priceThreshold?: number; annualRate?: number } = {}): Promise<any> {
    return (await this.api.get('/admin/reports/stock', { params })).data;
  }

  async exportStockReport(params: { clientId?: number; priceThreshold?: number; annualRate?: number } = {}): Promise<Blob> {
    return (await this.api.get('/admin/reports/stock/export', { params, responseType: 'blob' })).data;
  }

  async updateReportProductMinimum(productId: number, minimumStock: number): Promise<any> {
    return (await this.api.put(`/admin/reports/stock/products/${productId}/minimum`, { minimumStock })).data;
  }

  async getPriceIncreasesReport(): Promise<any> {
    return (await this.api.get('/admin/reports/price-increases')).data;
  }

  async getReportProductDocuments(productId: number): Promise<any> {
    return (await this.api.get(`/admin/reports/stock/products/${productId}/documents`)).data;
  }

  async resolvePurchaseDifference(lineId: number): Promise<any> {
    return (await this.api.post(`/admin/reports/stock/purchase-differences/${lineId}/resolve`)).data;
  }

  async createPhysicalStockCount(payload: { productId: number; countedQuantity: number; date?: string; note?: string }): Promise<any> {
    return (await this.api.post('/admin/reports/stock/counts', payload)).data;
  }

  async getReportProductMovements(productId: number): Promise<any> {
    return (await this.api.get(`/admin/reports/stock/products/${productId}/movements`)).data;
  }

  async getClientPrices(clientId: number): Promise<{ prices: any[] }> {
    return (await this.api.get(`/admin/clients/${clientId}/prices`)).data;
  }

  async createClientPrice(clientId: number, payload: any): Promise<{ price: any }> {
    return (await this.api.post(`/admin/clients/${clientId}/prices`, payload)).data;
  }

  async updateClientPrice(clientId: number, priceId: number, payload: any): Promise<{ price: any }> {
    return (await this.api.put(`/admin/clients/${clientId}/prices/${priceId}`, payload)).data;
  }

  async deleteClientPrice(clientId: number, priceId: number): Promise<void> {
    await this.api.delete(`/admin/clients/${clientId}/prices/${priceId}`);
  }
  // Cuentas corrientes (proveedores y clientes)
  async getAccountsSummary(partyType: 'supplier' | 'client'): Promise<{ items: any[] }> {
    return (await this.api.get('/admin/accounts', { params: { partyType } })).data;
  }

  async getCollectionTerms(clientId: number): Promise<{ terms: string }> {
    return (await this.api.get(`/admin/accounts/client/${clientId}/collection-terms`)).data;
  }

  async setCollectionTerms(clientId: number, terms: string): Promise<{ terms: string }> {
    return (await this.api.put(`/admin/accounts/client/${clientId}/collection-terms`, { terms })).data;
  }

  async createCollection(clientId: number, payload: { invoiceIds: number[]; paymentDate: string; note?: string; saveTerms?: string; payments: Array<{ category: string; amount: number; dueDate: string; checkNumber: string; checkBank: string }> }): Promise<{ movements: any[] }> {
    return (await this.api.post(`/admin/accounts/client/${clientId}/collections`, payload)).data;
  }

  async closeInvoiceDifference(clientId: number, invoiceId: number): Promise<{ movement: any }> {
    return (await this.api.post(`/admin/accounts/client/${clientId}/invoices/${invoiceId}/close-difference`)).data;
  }

  async getBusinessProjection(month?: string): Promise<any> {
    return (await this.api.get('/admin/business-projection', { params: month ? { month } : {} })).data;
  }

  async getPendingInvoices(): Promise<{ invoices: Array<{ invoiceId: number; clientId: number; clientName: string; number: string; date: string; amount: number; remaining: number; covered: number; days: number | null }> }> {
    return (await this.api.get('/admin/accounts/pending-invoices')).data;
  }

  async getCheques(): Promise<{ cheques: any[] }> {
    return (await this.api.get('/admin/accounts/cheques')).data;
  }

  async getAccountMovements(partyType: 'supplier' | 'client', partyId: number): Promise<{ movements: any[]; balance: number }> {
    return (await this.api.get(`/admin/accounts/${partyType}/${partyId}/movements`)).data;
  }

  async createAccountMovement(partyType: 'supplier' | 'client', partyId: number, payload: {
    movementType: 'documento' | 'pago';
    category: string;
    amount: number;
    movementDate: string;
    note?: string;
    receiptId?: number | null;
    checkNumber?: string;
    checkBank?: string;
    checkIssueDate?: string;
    checkDueDate?: string;
    checkStatus?: string;
    attachmentPath?: string;
    invoice?: {number:string;items:Array<{productName:string;microns?:number|null;quantity:number;monthlyOrderId:number|null;unitNet:number;vatRate:number}>}|null;
    allocations?: Array<{invoiceId:number;amount:number}>;
  }): Promise<{ movement: any }> {
    return (await this.api.post(`/admin/accounts/${partyType}/${partyId}/movements`, payload)).data;
  }

  async updateAccountMovement(id: number, payload: Record<string, any>): Promise<{ movement: any }> {
    return (await this.api.put(`/admin/accounts/movements/${id}`, payload)).data;
  }

  async updateAccountMovementCheckStatus(id: number, checkStatus: string): Promise<{ movement: any }> {
    return (await this.api.put(`/admin/accounts/movements/${id}/check-status`, { checkStatus })).data;
  }

  async deleteAccountMovement(id: number): Promise<{ message: string }> {
    return (await this.api.delete(`/admin/accounts/movements/${id}`)).data;
  }

  async createSupplierReceipt(supplierId: number, payload: {
    receiptDate: string;
    receiptNumber?: string;
    amount: number;
    note?: string;
    attachmentPath?: string;
    items: Array<{ ancho: number; largo: number; color: string; microns: number; unidad: 'bolsas' | 'packs'; cantidad: number; bolsasPorPack?: number; bolsasRecibidas?: number; bolsasCobradas?: number; unitPrice?: number; priceIsFigurative?: boolean; priceFigurativeNote?: string; itemNote?: string }>;
  }): Promise<{ receipt: any; movement: any }> {
    return (await this.api.post(`/admin/suppliers/${supplierId}/receipts`, payload)).data;
  }

  async getSupplierReceipt(receiptId: number): Promise<{ receipt: any }> {
    return (await this.api.get(`/admin/suppliers/receipts/${receiptId}`)).data;
  }

  async getSupplierReceipts(supplierId: number): Promise<{ receipts: any[] }> {
    return (await this.api.get(`/admin/suppliers/${supplierId}/receipts`)).data;
  }

  async getSupplierStock(supplierId: number): Promise<{ items: Array<{ productName: string; productColor: string; quantity: number; packs: number; looseBags: number }> }> {
    return (await this.api.get(`/admin/suppliers/${supplierId}/stock`)).data;
  }

  async getSupplierPrices(supplierId: number): Promise<SupplierPriceData> {
    return (await this.api.get(`/admin/suppliers/${supplierId}/prices`)).data;
  }

  async saveSupplierPrice(supplierId: number, payload: { ancho: number; largo: number; color: string; microns: number; unitPrice: number; note?: string }, priceId?: number): Promise<{ price: SupplierPrice }> {
    return (priceId ? await this.api.put(`/admin/suppliers/${supplierId}/prices/${priceId}`, payload) : await this.api.post(`/admin/suppliers/${supplierId}/prices`, payload)).data;
  }

  async deleteSupplierPrice(supplierId: number, priceId: number): Promise<{ message: string }> {
    return (await this.api.delete(`/admin/suppliers/${supplierId}/prices/${priceId}`)).data;
  }

  async importSupplierPricesFromReceipts(supplierId: number): Promise<{ added: number }> {
    return (await this.api.post(`/admin/suppliers/${supplierId}/prices/from-receipts`)).data;
  }

  async deleteSupplierReceipt(receiptId: number): Promise<{ message: string }> {
    return (await this.api.delete(`/admin/suppliers/receipts/${receiptId}`)).data;
  }

  async updateSupplierReceipt(receiptId: number, payload: {
    receiptDate: string;
    receiptNumber?: string;
    amount: number;
    note?: string;
    attachmentPath?: string;
    items: Array<{ ancho: number; largo: number; color: string; microns: number; unidad: 'bolsas' | 'packs'; cantidad: number; bolsasPorPack?: number; bolsasRecibidas?: number; bolsasCobradas?: number; unitPrice?: number; priceIsFigurative?: boolean; priceFigurativeNote?: string; itemNote?: string }>;
  }): Promise<{ receipt: any; movement: any }> {
    return (await this.api.put(`/admin/suppliers/receipts/${receiptId}`, payload)).data;
  }

  async uploadAccountAttachment(file: File): Promise<{ message: string; path: string }> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await this.api.post('/admin/accounts/upload-attachment', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }

  async getCurrencyRate(): Promise<CurrencyRate> {
    const response: AxiosResponse<CurrencyRate> = await this.api.get('/currency/usd-to-ars');
    return response.data;
  }

  async convertCurrency(amountUSD: number): Promise<CurrencyConversion> {
    const response: AxiosResponse<CurrencyConversion> = await this.api.post('/currency/convert', { amountUSD });
    return response.data;
  }

  async getCurrencyRates(): Promise<{ success: boolean; fallback?: boolean; rates: { buy: number; sell: number; average: number }; lastUpdated: string; source: string; message?: string }> {
    const response: AxiosResponse<{ success: boolean; fallback?: boolean; rates: { buy: number; sell: number; average: number }; lastUpdated: string; source: string; message?: string }> = await this.api.get('/currency/rates');
    return response.data;
  }

  async getSecurityOverview(): Promise<any> {
    return (await this.api.get('/admin/security/overview')).data;
  }

  async trackAnalytics(event: string, path: string): Promise<void> {
    const storageKey = 'polcen_analytics_visitor';
    let sessionId = localStorage.getItem(storageKey);
    if (!sessionId) {
      sessionId = Date.now().toString(36) + '_' + crypto.randomUUID().replace(/-/g, '');
      localStorage.setItem(storageKey, sessionId);
    }
    const params = new URLSearchParams(window.location.search);
    await this.api.post('/analytics/events', {
      sessionId,
      event,
      path,
      referrer: document.referrer,
      source: params.get('utm_source') || '',
      campaign: params.get('utm_campaign') || ''
    });
  }

  async getAnalyticsOverview(days = 30): Promise<any> {
    return (await this.api.get('/admin/analytics/overview', { params: { days } })).data;
  }

  async getSecurityEvents(params: any = {}): Promise<any> {
    return (await this.api.get('/admin/security/events', { params })).data;
  }

  async getSecurityAdmins(): Promise<any> {
    return (await this.api.get('/admin/security/admins')).data;
  }

  async revokeUserSessions(id: number): Promise<void> {
    await this.api.post(`/admin/security/users/${id}/revoke-sessions`);
  }

  async getBackups(): Promise<any> {
    return (await this.api.get('/admin/security/backups')).data;
  }

  async createBackup(): Promise<any> {
    return (await this.api.post('/admin/security/backups')).data;
  }

  async getBackupSettings(): Promise<any> {
    return (await this.api.get('/admin/security/backup-settings')).data;
  }

  async saveBackupSettings(settings: any): Promise<void> {
    await this.api.put('/admin/security/backup-settings', settings);
  }

  async downloadBackup(filename: string): Promise<Blob> {
    return (await this.api.get(`/admin/security/backups/${encodeURIComponent(filename)}`, { responseType: 'blob' })).data;
  }

  async deleteBackup(filename: string): Promise<void> {
    await this.api.delete(`/admin/security/backups/${encodeURIComponent(filename)}`);
  }

  // Métodos de utilidad
  async getPricingConfig(): Promise<{ config: any | null }> {
    const response: AxiosResponse<{ config: any | null }> = await this.api.get('/admin/pricing-config');
    return response.data;
  }

  async savePricingConfig(config: any): Promise<{ message: string }> {
    const response: AxiosResponse<{ message: string }> = await this.api.put('/admin/pricing-config', { config });
    return response.data;
  }

  async getMarketAnalysis(): Promise<{ items: any[] }> {
    return (await this.api.get('/admin/market-analysis')).data;
  }

  async createMarketAnalysis(payload: any): Promise<{ item: any }> {
    return (await this.api.post('/admin/market-analysis', payload)).data;
  }

  async createMarketAnalysisBatch(payload: { supplier: string; items: any[] }): Promise<{ items: any[]; count: number }> {
    return (await this.api.post('/admin/market-analysis/batch', payload)).data;
  }
  async updateMarketAnalysis(id: number, payload: any): Promise<{ item: any }> {
    return (await this.api.put(`/admin/market-analysis/${id}`, payload)).data;
  }

  async deleteMarketAnalysis(id: number): Promise<void> {
    await this.api.delete(`/admin/market-analysis/${id}`);
  }

  setAuthToken(token: string): void {
    localStorage.setItem('token', token);
  }

  removeAuthToken(): void {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  }

  getAuthToken(): string | null {
    return localStorage.getItem('token');
  }
}

// Crear instancia singleton
const apiService = new ApiService();
export default apiService;
